import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@vns/core';
import { useMaterialDetail } from '../lib/directory';
import { useLookups } from '../lib/lookups';
import {
  exportExcelSections,
  exportPdfSections,
  sharePdfSections,
  canShare,
  type ExportSection,
  type ExportMeta,
} from '../lib/export';
import { EmptyState, TableSkeleton, ErrorState, formatDate, initials, useToast } from './ui';
import {
  Truck,
  Box,
  Repeat,
  FileSpreadsheet,
  FileText,
  Share2,
  X,
  ExternalLink,
  Layers,
  Sparkles,
  DollarSign,
  Package,
} from 'lucide-react';

type Tab = 'purchases' | 'usage' | 'transfers';

const TABS: Array<{ key: Tab; label: string; icon: typeof Truck }> = [
  { key: 'purchases', label: 'Purchases & Billings', icon: Truck },
  { key: 'usage', label: 'Usage Log', icon: Box },
  { key: 'transfers', label: 'Stock Transfers', icon: Repeat },
];

const qty = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 3 });

export default function MaterialReportDrawer({
  materialId,
  materialName,
  onClose,
}: {
  materialId?: string;
  materialName?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: lk } = useLookups();
  const [tab, setTab] = useState<Tab>('purchases');
  const [busy, setBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);

  // If ID not passed directly, look it up by name from lookups
  const effectiveId =
    materialId ||
    lk?.materials.find(
      (m) => m.name.trim().toLowerCase() === (materialName ?? '').trim().toLowerCase(),
    )?.id ||
    '';

  const { data, isLoading, error, refetch } = useMaterialDetail(effectiveId);

  // Escape key & body scroll locking
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const unit = data?.unitName ? ` ${data.unitName}` : '';

  function buildSections(): ExportSection[] {
    if (!data) return [];
    return [
      {
        title: 'Purchases & Billings History',
        columns: [
          { key: 'sno', label: 'S.No', numeric: true },
          { key: 'date', label: 'Date' },
          { key: 'project', label: 'Project' },
          { key: 'supplier', label: 'Supplier' },
          { key: 'invoice', label: 'Invoice No.' },
          { key: 'quantity', label: 'Qty', numeric: true },
          { key: 'unit', label: 'Unit' },
          { key: 'rate', label: 'Rate', numeric: true, money: true },
          { key: 'amount', label: 'Total', numeric: true, money: true },
          { key: 'paid', label: 'Paid', numeric: true, money: true },
          { key: 'balance', label: 'Balance', numeric: true, money: true },
        ],
        rows: data.purchases.map((p, i) => ({
          sno: i + 1,
          date: formatDate(p.date),
          project: p.projectName,
          supplier: p.supplierName,
          invoice: p.invoiceNo || '—',
          quantity: qty(p.quantity),
          unit: data.unitName || '—',
          rate: formatMoney(p.ratePaise),
          amount: formatMoney(p.amountPaise),
          paid: formatMoney(p.advancePaidPaise),
          balance: formatMoney(p.balancePaise),
        })),
      },
      {
        title: 'Usage History',
        columns: [
          { key: 'sno', label: 'S.No', numeric: true },
          { key: 'date', label: 'Date' },
          { key: 'project', label: 'Project' },
          { key: 'quantity', label: 'Quantity Used', numeric: true },
          { key: 'balance', label: 'Stock Balance', numeric: true },
          { key: 'remarks', label: 'Remarks' },
        ],
        rows: data.usage.map((u, i) => ({
          sno: i + 1,
          date: formatDate(u.date),
          project: u.projectName,
          quantity: qty(u.quantity),
          balance: `${qty(u.balance)}${unit}`,
          remarks: u.remarks || '—',
        })),
      },
      {
        title: 'Stock Transfers',
        columns: [
          { key: 'sno', label: 'S.No', numeric: true },
          { key: 'date', label: 'Date' },
          { key: 'from', label: 'From Project' },
          { key: 'to', label: 'To Project' },
          { key: 'quantity', label: 'Quantity', numeric: true },
          { key: 'vehicle', label: 'Vehicle No.' },
          { key: 'driver', label: 'Driver' },
          { key: 'remarks', label: 'Remarks' },
        ],
        rows: data.transfers.map((t, i) => ({
          sno: i + 1,
          date: formatDate(t.date),
          from: t.fromProjectName,
          to: t.toProjectName,
          quantity: `${qty(t.quantity)}${unit}`,
          vehicle: t.vehicleNo || '—',
          driver: t.driverName || '—',
          remarks: t.remarks || '—',
        })),
      },
    ];
  }

  function buildMeta(): ExportMeta {
    return {
      title: `Material Report — ${data?.name ?? 'Material'}`,
      subtitle: `Rate: ${formatMoney(data?.ratePaise ?? 0)} · Stock on Hand: ${qty(data?.stockOnHand ?? 0)}${unit}`,
      filters: [
        `Supplier: ${data?.supplierName ?? '—'}`,
        `Brand: ${data?.brand ?? '—'}`,
        `Total Purchased: ${qty(data?.totalPurchased ?? 0)}${unit}`,
        `Total Used: ${qty(data?.totalUsed ?? 0)}${unit}`,
      ],
    };
  }

  async function runExport(kind: 'excel' | 'pdf') {
    if (!data) return;
    setBusy(kind);
    try {
      const sections = buildSections();
      const meta = buildMeta();
      if (kind === 'excel') {
        await exportExcelSections(sections, meta);
        toast.show('Material Report Excel downloaded');
      } else {
        await exportPdfSections(sections, meta);
        toast.show('Material Report PDF downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not export file', true);
    } finally {
      setBusy(null);
    }
  }

  async function runShare() {
    if (!data) return;
    setBusy('share');
    try {
      const sections = buildSections();
      const meta = buildMeta();
      const shared = await sharePdfSections(sections, meta);
      if (!shared) toast.show('Sharing is not supported on this device', true);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share file', true);
    } finally {
      setBusy(null);
    }
  }

  // Totals for purchase tab
  const totalPurchaseAmount = data?.purchases.reduce((s, p) => s + p.amountPaise, 0) ?? 0;
  const totalPurchasePaid = data?.purchases.reduce((s, p) => s + p.advancePaidPaise, 0) ?? 0;
  const totalPurchaseBalance = data?.purchases.reduce((s, p) => s + p.balancePaise, 0) ?? 0;
  const totalPurchaseQty = data?.purchases.reduce((s, p) => s + p.quantity, 0) ?? 0;

  // Totals for usage tab
  const totalUsageQty = data?.usage.reduce((s, u) => s + u.quantity, 0) ?? 0;

  // Totals for transfer tab
  const totalTransferQty = data?.transfers.reduce((s, t) => s + t.quantity, 0) ?? 0;

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside
        className="drawer drawer-wide bg-white/95 backdrop-blur-md shadow-2xl border-l border-slate-200/90 flex flex-col z-[60]"
        role="dialog"
        aria-modal="true"
        aria-label={data?.name ? `Material Report: ${data.name}` : 'Material Report'}
      >
        {/* ---------- drawer head ---------- */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-[10px] bg-gradient-to-tr from-sky-600 to-indigo-600 text-white flex items-center justify-center font-bold text-base shadow-xs shrink-0">
              {initials(data?.name ?? materialName ?? 'M')}
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 m-0 tracking-tight truncate">
                {data?.name ?? materialName ?? 'Material Report'}
              </h3>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 flex-wrap">
                {data?.unitName && (
                  <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-[4px] font-semibold text-[11px]">
                    Unit: {data.unitName}
                  </span>
                )}
                {data?.supplierName && (
                  <span className="flex items-center gap-1 text-slate-600">
                    <Truck className="w-3.5 h-3.5 text-slate-400" />
                    {data.supplierName}
                  </span>
                )}
                {data?.brand && <span>Brand: {data.brand}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {effectiveId && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold bg-white text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all shadow-xs"
                title="Open full dedicated material page"
                onClick={() => {
                  onClose();
                  navigate(`/materials/${effectiveId}`);
                }}
              >
                <ExternalLink className="w-3.5 h-3.5 text-sky-600" />
                <span className="hidden sm:inline">Full Page</span>
              </button>
            )}
            <button
              type="button"
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-[6px] hover:bg-slate-100 transition-colors"
              onClick={onClose}
              aria-label="Close Material Report"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ---------- drawer body ---------- */}
        <div className="drawer-body p-4 sm:p-5 flex-1 overflow-y-auto space-y-4">
          {isLoading ? (
            <TableSkeleton rows={6} cols={5} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data ? (
            <EmptyState
              icon="box"
              title="Material details not found"
              hint="Please verify that this material exists in the catalogue."
            />
          ) : (
            <>
              {/* KPI metrics row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-[8px] bg-slate-50/80 border border-slate-200/80">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Current Rate
                  </div>
                  <div className="text-sm sm:text-base font-mono font-bold text-slate-900 mt-1 tabular-nums">
                    {formatMoney(data.ratePaise)}
                  </div>
                </div>

                <div className="p-3 rounded-[8px] bg-slate-50/80 border border-slate-200/80">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Total Purchased
                  </div>
                  <div className="text-sm sm:text-base font-mono font-bold text-slate-900 mt-1 tabular-nums">
                    {qty(data.totalPurchased)}
                    <span className="text-xs font-normal text-slate-500 ml-1">{unit}</span>
                  </div>
                </div>

                <div className="p-3 rounded-[8px] bg-slate-50/80 border border-slate-200/80">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Total Used
                  </div>
                  <div className="text-sm sm:text-base font-mono font-bold text-slate-900 mt-1 tabular-nums">
                    {qty(data.totalUsed)}
                    <span className="text-xs font-normal text-slate-500 ml-1">{unit}</span>
                  </div>
                </div>

                <div className="p-3 rounded-[8px] bg-slate-50/80 border border-slate-200/80">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Stock on Hand
                  </div>
                  <div
                    className={`text-sm sm:text-base font-mono font-bold mt-1 tabular-nums ${
                      data.stockOnHand > 0 ? 'text-emerald-600' : 'text-slate-400'
                    }`}
                  >
                    {qty(data.stockOnHand)}
                    <span className="text-xs font-normal text-slate-500 ml-1">{unit}</span>
                  </div>
                </div>
              </div>

              {/* Tabs + Export Toolbar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-[8px] border border-slate-200/70 overflow-x-auto">
                  {TABS.map((t) => {
                    const active = tab === t.key;
                    const IconComp = t.icon;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] text-xs font-semibold whitespace-nowrap transition-all ${
                          active
                            ? 'bg-white text-sky-700 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                        }`}
                      >
                        <IconComp className={`w-3.5 h-3.5 ${active ? 'text-sky-600' : 'text-slate-400'}`} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>

                {/* Export Buttons */}
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    type="button"
                    disabled={!data || !!busy}
                    onClick={() => void runExport('excel')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all disabled:opacity-50"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                    {busy === 'excel' ? 'Preparing…' : 'Excel'}
                  </button>

                  <button
                    type="button"
                    disabled={!data || !!busy}
                    onClick={() => void runExport('pdf')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all disabled:opacity-50"
                  >
                    <FileText className="w-3.5 h-3.5 text-rose-600" />
                    {busy === 'pdf' ? 'Preparing…' : 'PDF'}
                  </button>

                  {canShare() && (
                    <button
                      type="button"
                      disabled={!data || !!busy}
                      onClick={() => void runShare()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all disabled:opacity-50"
                    >
                      <Share2 className="w-3.5 h-3.5 text-indigo-600" />
                      {busy === 'share' ? 'Sharing…' : 'Share'}
                    </button>
                  )}
                </div>
              </div>

              {/* Tab Data Table */}
              <div className="bg-white rounded-[10px] border border-slate-200/80 shadow-xs overflow-hidden">
                <div className="overflow-x-auto">
                  {tab === 'purchases' &&
                    (!data.purchases.length ? (
                      <div className="p-8">
                        <EmptyState icon="truck" title="No purchases recorded yet" />
                      </div>
                    ) : (
                      <table className="w-full text-xs text-slate-800">
                        <thead className="bg-slate-50/90 border-b border-slate-200/80 text-slate-600 font-bold uppercase text-[11px] tracking-wider">
                          <tr>
                            <th className="py-2.5 px-3 text-center w-12">S.No</th>
                            <th className="py-2.5 px-3 text-center">Date</th>
                            <th className="py-2.5 px-3 text-center">Project</th>
                            <th className="py-2.5 px-3 text-center">Supplier</th>
                            <th className="py-2.5 px-3 text-center">Invoice No.</th>
                            <th className="py-2.5 px-3 text-center">Quantity</th>
                            <th className="py-2.5 px-3 text-center">Rate</th>
                            <th className="py-2.5 px-3 text-center">Total Amount</th>
                            <th className="py-2.5 px-3 text-center">Paid</th>
                            <th className="py-2.5 px-3 text-center">Balance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {data.purchases.map((p, idx) => (
                            <tr key={p.id} className="hover:bg-slate-50/60 transition-colors">
                              <td className="py-2.5 px-3 text-center text-slate-500">{idx + 1}</td>
                              <td className="py-2.5 px-3 text-center">{formatDate(p.date)}</td>
                              <td
                                className="py-2.5 px-3 text-center font-semibold text-sky-700 cursor-pointer hover:underline"
                                onClick={() => {
                                  onClose();
                                  navigate(`/projects/${p.projectId}`);
                                }}
                              >
                                {p.projectName}
                              </td>
                              <td className="py-2.5 px-3 text-center">{p.supplierName}</td>
                              <td className="py-2.5 px-3 text-center font-mono">{p.invoiceNo || '—'}</td>
                              <td className="py-2.5 px-3 text-center font-mono font-bold tabular-nums">
                                {qty(p.quantity)}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono tabular-nums">
                                {formatMoney(p.ratePaise)}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono font-bold tabular-nums text-slate-900">
                                {formatMoney(p.amountPaise)}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono font-semibold tabular-nums text-emerald-600">
                                {formatMoney(p.advancePaidPaise)}
                              </td>
                              <td
                                className={`py-2.5 px-3 text-center font-mono font-bold tabular-nums ${
                                  p.balancePaise > 0 ? 'text-rose-600' : 'text-slate-400'
                                }`}
                              >
                                {formatMoney(p.balancePaise)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50/90 border-t border-slate-200/80 font-bold">
                          <tr>
                            <td colSpan={5} className="py-2.5 px-3 text-center">
                              Total ({data.purchases.length} records)
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono tabular-nums">
                              {qty(totalPurchaseQty)}
                            </td>
                            <td className="py-2.5 px-3 text-center">—</td>
                            <td className="py-2.5 px-3 text-center font-mono tabular-nums text-slate-900">
                              {formatMoney(totalPurchaseAmount)}
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono tabular-nums text-emerald-600">
                              {formatMoney(totalPurchasePaid)}
                            </td>
                            <td
                              className={`py-2.5 px-3 text-center font-mono tabular-nums ${
                                totalPurchaseBalance > 0 ? 'text-rose-600' : 'text-slate-400'
                              }`}
                            >
                              {formatMoney(totalPurchaseBalance)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ))}

                  {tab === 'usage' &&
                    (!data.usage.length ? (
                      <div className="p-8">
                        <EmptyState icon="box" title="No usage logged yet" />
                      </div>
                    ) : (
                      <table className="w-full text-xs text-slate-800">
                        <thead className="bg-slate-50/90 border-b border-slate-200/80 text-slate-600 font-bold uppercase text-[11px] tracking-wider">
                          <tr>
                            <th className="py-2.5 px-3 text-center w-12">S.No</th>
                            <th className="py-2.5 px-3 text-center">Date</th>
                            <th className="py-2.5 px-3 text-center">Project</th>
                            <th className="py-2.5 px-3 text-center">Quantity Used</th>
                            <th className="py-2.5 px-3 text-center">Stock Balance After Entry</th>
                            <th className="py-2.5 px-3 text-center">Remarks / Activity</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {data.usage.map((u, idx) => (
                            <tr key={u.id} className="hover:bg-slate-50/60 transition-colors">
                              <td className="py-2.5 px-3 text-center text-slate-500">{idx + 1}</td>
                              <td className="py-2.5 px-3 text-center">{formatDate(u.date)}</td>
                              <td
                                className="py-2.5 px-3 text-center font-semibold text-sky-700 cursor-pointer hover:underline"
                                onClick={() => {
                                  onClose();
                                  navigate(`/projects/${u.projectId}`);
                                }}
                              >
                                {u.projectName}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono font-bold tabular-nums">
                                {qty(u.quantity)}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono tabular-nums">
                                {qty(u.balance)}
                                {unit}
                              </td>
                              <td className="py-2.5 px-3 text-center text-slate-500">{u.remarks || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50/90 border-t border-slate-200/80 font-bold">
                          <tr>
                            <td colSpan={3} className="py-2.5 px-3 text-center">
                              Total ({data.usage.length} records)
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono tabular-nums">
                              {qty(totalUsageQty)}
                            </td>
                            <td colSpan={2} className="py-2.5 px-3 text-center" />
                          </tr>
                        </tfoot>
                      </table>
                    ))}

                  {tab === 'transfers' &&
                    (!data.transfers.length ? (
                      <div className="p-8">
                        <EmptyState icon="repeat" title="No stock transfers yet" />
                      </div>
                    ) : (
                      <table className="w-full text-xs text-slate-800">
                        <thead className="bg-slate-50/90 border-b border-slate-200/80 text-slate-600 font-bold uppercase text-[11px] tracking-wider">
                          <tr>
                            <th className="py-2.5 px-3 text-center w-12">S.No</th>
                            <th className="py-2.5 px-3 text-center">Date</th>
                            <th className="py-2.5 px-3 text-center">From Project</th>
                            <th className="py-2.5 px-3 text-center">To Project</th>
                            <th className="py-2.5 px-3 text-center">Quantity</th>
                            <th className="py-2.5 px-3 text-center">Vehicle No.</th>
                            <th className="py-2.5 px-3 text-center">Driver</th>
                            <th className="py-2.5 px-3 text-center">Remarks</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {data.transfers.map((t, idx) => (
                            <tr key={t.id} className="hover:bg-slate-50/60 transition-colors">
                              <td className="py-2.5 px-3 text-center text-slate-500">{idx + 1}</td>
                              <td className="py-2.5 px-3 text-center">{formatDate(t.date)}</td>
                              <td className="py-2.5 px-3 text-center">{t.fromProjectName}</td>
                              <td className="py-2.5 px-3 text-center">{t.toProjectName}</td>
                              <td className="py-2.5 px-3 text-center font-mono font-bold tabular-nums">
                                {qty(t.quantity)}
                                {unit}
                              </td>
                              <td className="py-2.5 px-3 text-center font-mono">{t.vehicleNo || '—'}</td>
                              <td className="py-2.5 px-3 text-center">{t.driverName || '—'}</td>
                              <td className="py-2.5 px-3 text-center text-slate-500">{t.remarks || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50/90 border-t border-slate-200/80 font-bold">
                          <tr>
                            <td colSpan={4} className="py-2.5 px-3 text-center">
                              Total ({data.transfers.length} records)
                            </td>
                            <td className="py-2.5 px-3 text-center font-mono tabular-nums">
                              {qty(totalTransferQty)}
                              {unit}
                            </td>
                            <td colSpan={3} className="py-2.5 px-3 text-center" />
                          </tr>
                        </tfoot>
                      </table>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ---------- drawer foot ---------- */}
        <div className="p-3.5 sm:p-4 bg-slate-50/70 border-t border-slate-200/80 flex items-center justify-end">
          <button
            type="button"
            className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-[8px] bg-white text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all shadow-xs"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </aside>
    </>
  );
}
